import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../environments/environmentCAS';

export interface NeolianeFormula {
  formulaId: string;
  code: string;
  label: string;
  description: string;
  estimatedPrice?: number | null;
}

export interface NeolianeProduct {
  productId: string;
  name: string;
  type: string;
  formulas: NeolianeFormula[];
}

@Injectable({
  providedIn: 'root'
})
export class NeolianeService {
  private baseUrl = `${environment.casApiUrl}${environment.apiEndpoint}/v1/neoliane`;

  constructor(private http: HttpClient) {}

  /**
   * Récupérer la liste des produits / formules Néoliane exposés par le backend
   * GET /api/v1/neoliane/simple/products
   */
  getProducts(): Observable<NeolianeProduct[]> {
    const url = `${this.baseUrl}/simple/products`;
    return this.http.get<NeolianeProduct[]>(url);
  }

  /**
   * Estimation rapide de prix pour un produit / formule
   * GET /api/v1/neoliane/tarification/estimate?productId=...&formulaId=...&members=1
   */
  getQuickEstimate(productId: string, formulaId: string, members: number = 1, zipcode?: string, birthyear?: string, regime?: string): Observable<any> {
    const url = `${this.baseUrl}/tarification/estimate`;
    let params = new HttpParams()
      .set('productId', productId)
      .set('formulaId', formulaId)
      .set('members', String(members));

    if (zipcode) params = params.set('zipcode', zipcode);
    if (birthyear) params = params.set('birthyear', birthyear);
    if (regime) params = params.set('regime', regime);

    return this.http.get<any>(url, { params });
  }

  /**
   * Tarification simple via l'API Néoliane Simple
   * POST /api/v1/neoliane/simple/tarification
   */
  getSimpleTarification(payload: {
    effectdate: string;
    postalcode: string;
    birthdate_adult1: string;
    regime_adult1: string;
    producttype?: string;
    gammes?: string;    // comma-separated product IDs according to Neoliane doc
    formules?: string;  // comma-separated formula IDs according to Neoliane doc
  }): Observable<any> {
    const url = `${this.baseUrl}/simple/tarification`;
    return this.http.post<any>(url, payload);
  }

  downloadPrefilledDocuments(subscriptionId: string): Observable<Blob> {
    const url = `${this.baseUrl}/subscription/${encodeURIComponent(subscriptionId)}/documents`;
    return this.http.get(url, {
      responseType: 'blob'
    });
  }

  /**
   * Générer un devis Néoliane (PDF)
   * POST /api/v1/neoliane/quote/generate
   */
  generateQuote(payload: { productId: string; formulaId: string; personalInfo: any }): Observable<Blob> {
    const url = `${this.baseUrl}/quote/generate`;
    return this.http.post(url, payload, {
      responseType: 'blob'
    });
  }

  /**
   * Envoyer le devis Néoliane par email
   * POST /api/v1/neoliane/quote/send-email
   */
  sendQuoteByEmail(payload: { productId: string; formulaId: string; personalInfo: any; recipientEmail?: string }): Observable<any> {
    const url = `${this.baseUrl}/quote/send-email`;
    return this.http.post<any>(url, payload);
  }

  // ==================== SUBSCRIPTION METHODS ====================

  /**
   * Étape 1: Création du panier
   * POST /api/v1/neoliane/subscription/cart
   */
  createCart(cartRequest: any): Observable<any> {
    const url = `${this.baseUrl}/subscription/cart`;
    return this.http.post<any>(url, cartRequest);
  }

  /**
   * Étape 2: Création de la souscription
   * POST /api/v1/neoliane/subscription
   */
  createSubscription(subscriptionRequest: { lead_id: string; signtype: string; features?: string[] }): Observable<any> {
    const url = `${this.baseUrl}/subscription`;
    return this.http.post<any>(url, subscriptionRequest);
  }

  /**
   * Récupérer une souscription
   * GET /api/v1/neoliane/subscription/{id}
   */
  getSubscription(id: string): Observable<any> {
    const url = `${this.baseUrl}/subscription/${id}`;
    return this.http.get<any>(url);
  }

  /**
   * Étape 3: StepConcern - Informations des adhérents
   * PUT /api/v1/neoliane/subscription/{id}/stepconcern/{stepId}
   */
  submitStepConcern(id: string, stepId: string, concernData: any): Observable<any> {
    const url = `${this.baseUrl}/subscription/${id}/stepconcern/${stepId}`;
    return this.http.put<any>(url, concernData);
  }

  /**
   * Étape 4: StepBank - Informations bancaires
   * PUT /api/v1/neoliane/subscription/{id}/stepbank/{stepId}
   */
  submitStepBank(id: string, stepId: string, bankData: any): Observable<any> {
    const url = `${this.baseUrl}/subscription/${id}/stepbank/${stepId}`;
    return this.http.put<any>(url, bankData);
  }

  /**
   * Étape 5: Upload d'un document (BA, SEPA, Mandat)
   * POST /api/v1/neoliane/subscription/{id}/document
   */
  uploadDocument(id: string, documentData: { type: string; content: string; contract_ids: string[]; concern?: string }): Observable<any> {
    const url = `${this.baseUrl}/subscription/${id}/document`;
    return this.http.post<any>(url, documentData);
  }

  /**
   * Étape 6: Validation d'un contrat
   * PUT /api/v1/neoliane/subscription/contract/{contractId}/validate
   */
  validateContract(contractId: string): Observable<any> {
    const url = `${this.baseUrl}/subscription/contract/${contractId}/validate`;
    return this.http.put<any>(url, []);
  }

  /**
   * Télécharger les documents pré-remplis
   * GET /api/v1/neoliane/subscription/{id}/documentdownload
   */
  downloadSubscriptionDocuments(id: string): Observable<Blob> {
    const url = `${this.baseUrl}/subscription/${id}/documentdownload`;
    return this.http.get(url, { responseType: 'blob' });
  }

  /**
   * Workflow intelligent complet
   * POST /api/v1/neoliane/subscription/smart
   * 
   * Automatise: Cart -> Subscription -> StepConcern -> StepBank
   */
  startSmartSubscription(payload: {
    cart: any;
    concern: any;
    bank: any;
  }): Observable<any> {
    const url = `${this.baseUrl}/subscription/smart`;
    return this.http.post<any>(url, payload);
  }

  // ==================== EZ API METHODS (NEW) ====================

  /**
   * Créer une offre via l'API EZ (simplifiée)
   * POST /api/v1/neoliane/ez/offer
   * 
   * Un seul appel crée toute la souscription
   */
  createEzOffer(offerData: {
    profileId: string;
    prospectType?: string;
    persons: any[];
    bank: any;
    address: any;
    brokerageFeeAmount?: number;
  }): Observable<any> {
    const url = `${this.baseUrl}/ez/offer`;
    return this.http.post<any>(url, offerData);
  }

  /**
   * Créer une offre Santé simplifiée via l'API EZ
   * POST /api/v1/neoliane/ez/health
   */
  createEzHealthOffer(payload: {
    profileId: string;
    personalInfo: any;
    bankInfo: any;
    addressInfo: any;
  }): Observable<any> {
    const url = `${this.baseUrl}/ez/health`;
    return this.http.post<any>(url, payload);
  }

  /**
   * Mettre à jour une offre EZ
   * PUT /api/v1/neoliane/ez/offer/{offerId}
   */
  updateEzOffer(offerId: string, offerData: any): Observable<any> {
    const url = `${this.baseUrl}/ez/offer/${offerId}`;
    return this.http.put<any>(url, offerData);
  }

  /**
   * Récupérer une offre EZ
   * GET /api/v1/neoliane/ez/offer/{offerId}
   */
  getEzOffer(offerId: string): Observable<any> {
    const url = `${this.baseUrl}/ez/offer/${offerId}`;
    return this.http.get<any>(url);
  }

  /**
   * Workflow complet de souscription EZ en un seul appel
   * POST /api/v1/neoliane/ez/subscription
   * 
   * Gère automatiquement: profile → generateprices → cart → offer
   */
  fullEzSubscription(payload: {
    profile: {
      zipCode: string;
      dateEffect: string;
      productType: string;
      profileHealth: Array<{
        socialSecurityScheme: string;
        birthYear: string;
        familyMember: string;
      }>;
    };
    productTypes?: string[];
    offer: {
      prospectType?: string;
      persons: any[];
      bank: any;
      address: any;
    };
  }): Observable<any> {
    const url = `${this.baseUrl}/ez/subscription`;
    return this.http.post<any>(url, payload);
  }

  /**
   * Étape 1: Créer un profil EZ
   * POST /api/v1/neoliane/ez/profile
   */
  createEzProfile(profileData: {
    zipCode: string;
    dateEffect: string;
    productType: string;
    profileHealth: Array<{
      socialSecurityScheme: string;
      birthYear: string;
      familyMember: string;
    }>;
  }): Observable<any> {
    const url = `${this.baseUrl}/ez/profile`;
    return this.http.post<any>(url, profileData);
  }

  /**
   * Étape 2: Générer les tarifs
   * POST /api/v1/neoliane/ez/profile/{profileId}/generateprices
   */
  generateEzPrices(profileId: string, types: string[] = ['sante']): Observable<any> {
    const url = `${this.baseUrl}/ez/profile/${profileId}/generateprices`;
    return this.http.post<any>(url, { types });
  }

  /**
   * Étape 3: Créer le panier
   * POST /api/v1/neoliane/ez/profile/{profileId}/cart
   */
  createEzCart(profileId: string, products: Array<{ pricingId: string; members?: string[] }>): Observable<any> {
    const url = `${this.baseUrl}/ez/profile/${profileId}/cart`;
    return this.http.post<any>(url, products);
  }
}
